import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Cliente API centralizado con configuración consistente
 * Maneja autenticación, errores y configuración base
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.client.interceptors.request.use(
      async (config) => {
          // El token se agregará dinámicamente en cada llamada
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para manejar errores globalmente
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        this.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Maneja errores de forma consistente
   * Nota: El uso de message estático está deprecado en Ant Design v5+
   * Los componentes deben usar App.useApp() para obtener message
   * Este método solo registra errores en consola para evitar warnings
   */
  private handleError(error: any): void {
    // Solo registrar en consola para evitar warnings de Ant Design
    // Los componentes manejarán los mensajes de error usando App.useApp()
    if (error.response) {
      const status = error.response.status;
      const errorMessage = error.response.data?.message || error.response.data?.detail || 'Error en la solicitud';
      console.error(`API Error ${status}:`, errorMessage);
    } else if (error.request) {
      console.error('Network Error:', error.request);
    } else {
      console.error('Request Error:', error.message);
    }
  }

  /**
   * Realiza una petición GET
   */
  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig & { token?: string; showErrorMessage?: boolean }
  ): Promise<AxiosResponse<T>> {
    const { showErrorMessage, token, ...restConfig } = config || {};
    const headers = {
      ...restConfig?.headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    return this.client.get<T>(url, {
      ...restConfig,
      headers,
    });
  }

  /**
   * Realiza una petición POST
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig & { token?: string }
  ): Promise<AxiosResponse<T>> {
    const headers = {
      ...config?.headers,
      ...(config?.token && { Authorization: `Bearer ${config.token}` }),
    };

    return this.client.post<T>(url, data, {
      ...config,
      headers,
    });
  }

  /**
   * Realiza una petición PUT
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig & { token?: string }
  ): Promise<AxiosResponse<T>> {
    const headers = {
      ...config?.headers,
      ...(config?.token && { Authorization: `Bearer ${config.token}` }),
    };

    return this.client.put<T>(url, data, {
      ...config,
      headers,
    });
  }

  /**
   * Realiza una petición DELETE
   */
  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig & { token?: string }
  ): Promise<AxiosResponse<T>> {
    const headers = {
      ...config?.headers,
      ...(config?.token && { Authorization: `Bearer ${config.token}` }),
    };

    return this.client.delete<T>(url, {
      ...config,
      headers,
    });
  }
}

export const apiClient = new ApiClient();

